import { Tag } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useTicketCategories } from '../../hooks/queries/useTicketCategories';

interface CompetenceChipsProps {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export function CompetenceChips({ value, onChange, disabled = false }: CompetenceChipsProps) {
  const { data: categories = [], isLoading } = useTicketCategories(false);

  function toggleCategory(id: string) {
    if (disabled) return;
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-7 w-24 rounded-full bg-neutral-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <p className="text-sm text-neutral-400 italic">
        Aucune catégorie disponible.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((cat) => {
        const isSelected = value.includes(cat.id);
        return (
          <button
            key={cat.id}
            type="button"
            disabled={disabled}
            onClick={() => toggleCategory(cat.id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary focus-visible:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isSelected
                ? 'bg-cime-primary text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
            )}
            aria-pressed={isSelected}
          >
            <Tag className="w-3 h-3 shrink-0" aria-hidden />
            {cat.label}
          </button>
        );
      })}
    </div>
  );
}
